import type { CurvaDocument } from "@/document/model/document";
import { getSceneNodeChildren } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import type { Rect } from "@/lib/math/rect";

export type AlignmentAction = "left" | "horizontalCenter" | "right" | "top" | "verticalCenter" | "bottom";
type DistributionAction = "horizontal" | "vertical";

export const alignmentLabels: Record<AlignmentAction, string> = {
  left: "Align left",
  horizontalCenter: "Align horizontal centers",
  right: "Align right",
  top: "Align top",
  verticalCenter: "Align vertical centers",
  bottom: "Align bottom",
};

export function getAlignmentUpdates(nodes: SceneNode[], selectionBounds: Rect, action: AlignmentAction, visualBoundsOverrides?: Map<string, Rect>): Array<{ nodeId: string; bounds: Rect }> {
  return nodes.map(node => {
    const visualBounds = visualBoundsOverrides?.get(node.id) ?? node.bounds;
    return {
      nodeId: node.id,
      bounds: alignBoundsWithVisual(node.bounds, visualBounds, selectionBounds, action),
    };
  });
}

export function getAlignmentReferenceBounds(document: CurvaDocument, nodeIds: string[], selectionBounds?: Rect): Rect | undefined {
  if (nodeIds.length > 1) {
    return selectionBounds;
  }

  const nodeId = nodeIds[0];
  if (!nodeId) {
    return undefined;
  }

  return findDirectParentAlignmentContainer(document, nodeId)?.bounds;
}

export function getDistributionUpdates(nodes: SceneNode[], action: DistributionAction): Array<{ nodeId: string; bounds: Rect }> {
  const axis = action === "horizontal" ? "x" : "y";
  const sizeKey = action === "horizontal" ? "width" : "height";
  const sortedNodes = [...nodes].sort((left, right) => left.bounds[axis] - right.bounds[axis]);
  const first = sortedNodes[0];
  const last = sortedNodes.at(-1);
  if (!first || !last || sortedNodes.length < 3) {
    return [];
  }

  const start = first.bounds[axis];
  const end = last.bounds[axis] + last.bounds[sizeKey];
  const totalSize = sortedNodes.reduce((sum, node) => sum + node.bounds[sizeKey], 0);
  const gap = (end - start - totalSize) / (sortedNodes.length - 1);
  let cursor = start;

  return sortedNodes.map(node => {
    const nextBounds = { ...node.bounds, [axis]: cursor };
    cursor += node.bounds[sizeKey] + gap;
    return { nodeId: node.id, bounds: nextBounds };
  });
}

function alignBoundsWithVisual(actualBounds: Rect, visualBounds: Rect, selectionBounds: Rect, action: AlignmentAction): Rect {
  const aligned = alignBounds(visualBounds, selectionBounds, action);
  return {
    ...actualBounds,
    x: aligned.x + (actualBounds.x - visualBounds.x),
    y: aligned.y + (actualBounds.y - visualBounds.y),
  };
}

function alignBounds(bounds: Rect, selectionBounds: Rect, action: AlignmentAction): Rect {
  switch (action) {
    case "left":
      return { ...bounds, x: selectionBounds.x };
    case "horizontalCenter":
      return { ...bounds, x: selectionBounds.x + (selectionBounds.width - bounds.width) / 2 };
    case "right":
      return { ...bounds, x: selectionBounds.x + selectionBounds.width - bounds.width };
    case "top":
      return { ...bounds, y: selectionBounds.y };
    case "verticalCenter":
      return { ...bounds, y: selectionBounds.y + (selectionBounds.height - bounds.height) / 2 };
    case "bottom":
      return { ...bounds, y: selectionBounds.y + selectionBounds.height - bounds.height };
  }
}

function findDirectParentAlignmentContainer(document: CurvaDocument, nodeId: string): SceneNode | undefined {
  for (const page of document.pages) {
    const parent = findDirectParentAlignmentContainerInNodes(page.children, nodeId, undefined);
    if (parent !== NOT_FOUND) {
      return parent;
    }
  }

  return undefined;
}

const NOT_FOUND = Symbol("not-found");

function findDirectParentAlignmentContainerInNodes(
  nodes: SceneNode[],
  nodeId: string,
  parentContainer: SceneNode | undefined,
): SceneNode | undefined | typeof NOT_FOUND {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return parentContainer;
    }

    const children = getSceneNodeChildren(node);
    if (!children) {
      continue;
    }

    const found = findDirectParentAlignmentContainerInNodes(children, nodeId, node);
    if (found !== NOT_FOUND) {
      return found;
    }
  }

  return NOT_FOUND;
}
